import { Link, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type LoadTrailerPlanVehicle, type TrailerLayoutPosition } from '../lib/api'
import { useAuth } from '../lib/auth-context'
import { SupportReadOnlyNotice, useSupportReadOnlyView } from '../lib/support-session'
import { VisualTrailer as TrailerDiagram } from '../components/trailer/VisualTrailer'
import { publishLoadStatusChanged, subscribeToLoadStatusChanges } from '../lib/load-status-sync'

const STATUS_OPTIONS = ['DRAFT', 'READY', 'AUTO_SEQUENCED', 'LOCKED']

type VehicleDraft = {
  trailerPosition: string
  pickupSequence: string
  deliverySequence: string
  notes: string
}

type WarningSeverity = 'critical' | 'warning' | 'info'

type SlotProfileDraft = {
  maxLengthInches: string
  maxHeightInches: string
  maxWeightLbs: string
  allowInoperable: boolean
}

type RecommendationState = {
  status: string
  score: number
  warnings: string[]
  recommendations: Array<{
    vehicleId: string
    vin?: string | null
    recommendedPosition: string | null
    confidence: number
    reason: string
  }>
  leverHeights: Record<string, number>
  applied: boolean
}

function vehicleLabel(vehicle: LoadTrailerPlanVehicle) {
  const details = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')
  return details || vehicle.vin || vehicle.vehicleId
}

function normalizeSlot(value: string) {
  return value.trim().toUpperCase()
}

function clampLeverValue(raw: string) {
  if (!raw) return ''
  const parsed = Number(raw)
  if (!Number.isFinite(parsed)) return ''
  return String(Math.min(7, Math.max(1, Math.round(parsed))))
}

function warningSeverity(message: string): WarningSeverity {
  const value = message.toLowerCase()
  if (value.includes('duplicate') || value.includes('conflict') || value.includes('overweight') || value.includes('invalid')) {
    return 'critical'
  }
  if (value.includes('missing sequence') || value.includes('missing') || value.includes('unassigned') || value.includes('empty')) {
    return 'warning'
  }
  return 'info'
}

function buildSlotProfileDraft(
  positions: TrailerLayoutPosition[],
  profiles?: Record<string, { maxLengthInches?: number; maxHeightInches?: number; maxWeightLbs?: number; allowInoperable?: boolean }>
) {
  const out: Record<string, SlotProfileDraft> = {}
  for (const position of positions) {
    const profile = profiles?.[position.id]
    out[position.id] = {
      maxLengthInches: String(profile?.maxLengthInches ?? position.maxLengthInches ?? ''),
      maxHeightInches: String(profile?.maxHeightInches ?? position.maxHeightInches ?? ''),
      maxWeightLbs: String(profile?.maxWeightLbs ?? position.maxWeightLbs ?? ''),
      allowInoperable: typeof profile?.allowInoperable === 'boolean'
        ? profile.allowInoperable
        : Boolean(position.allowInoperable),
    }
  }
  return out
}


export default function LoadTrailerPlannerPage() {
  const { loadId } = useParams<{ loadId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { hasRole } = useAuth()
  const { isSupportReadOnlyView } = useSupportReadOnlyView()

  const canInternalPlan = hasRole('DISPATCHER', 'BROKER_ADMIN', 'DEALER', 'SHIPPER', 'ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'BROKER')

  const [plannerStatus, setPlannerStatus] = useState('DRAFT')
  const [plannerNotes, setPlannerNotes] = useState('')
  const [layoutMode, setLayoutMode] = useState('semi-10-car-hauler')
  const [vehicleOrder, setVehicleOrder] = useState<string[]>([])
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null)
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null)
  const [leverHeights, setLeverHeights] = useState<Record<string, string>>({})
  const [deckStates, setDeckStates] = useState<Record<string, { height: number; angle: number }>>({})
  const [rampStates, setRampStates] = useState<Record<string, 'stored' | 'deployed' | 'raised' | 'lowered'>>({})
  const [vehicleDraft, setVehicleDraft] = useState<Record<string, VehicleDraft>>({})
  const [selectedCarrierId, setSelectedCarrierId] = useState('')
  const [selectedDriverId, setSelectedDriverId] = useState('')
  const [assignmentNotes, setAssignmentNotes] = useState('')
  const [slotProfiles, setSlotProfiles] = useState<Record<string, SlotProfileDraft>>({})
  const [recommendation, setRecommendation] = useState<RecommendationState | null>(null)
  const [recommendPending, setRecommendPending] = useState(false)
  const [applyRecommendationPending, setApplyRecommendationPending] = useState(false)
  const [tripLayoutName, setTripLayoutName] = useState('')
  const [applyLayoutPendingId, setApplyLayoutPendingId] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [draftBase, setDraftBase] = useState<{ loadId: string; updatedAt: string | null } | null>(null)
  const [saveFeedback, setSaveFeedback] = useState<'idle' | 'saved' | 'error'>('idle')
  const [saveFeedbackText, setSaveFeedbackText] = useState('')

  useEffect(() => {
    if (canInternalPlan) return
    if (hasRole('DRIVER')) {
      navigate('/driver/jobs', { replace: true })
      return
    }
    if (hasRole('CARRIER', 'CARRIER_ADMIN')) {
      navigate('/carrier/dashboard', { replace: true })
    }
  }, [canInternalPlan, hasRole, navigate])

  const planQuery = useQuery({
    queryKey: ['load-trailer-plan', loadId],
    queryFn: async () => (await api.loads.getTrailerPlan(loadId!)).data,
    enabled: !!loadId,
    staleTime: 1000,
    refetchInterval: 12000,
  })

  const isTerminalPlan = [planQuery.data?.status, (planQuery.data as any)?.operationalStatus].some(
    status => ['DELIVERED', 'COMPLETED', 'CANCELLED', 'PAID'].includes(String(status).toUpperCase())
  )
  const canEditTrailerPlan = canInternalPlan && !isSupportReadOnlyView && !isTerminalPlan

  useEffect(() => loadId ? subscribeToLoadStatusChanges(loadId, () => { void planQuery.refetch() }) : undefined, [loadId])

  const assignmentQuery = useQuery({
    queryKey: ['load-assignment-options', loadId],
    queryFn: async () => (await api.loads.assignmentOptions(loadId!)).data,
    enabled: !!loadId,
    refetchInterval: 10000,
  })

  const vehicles = planQuery.data?.vehicles ?? []

  const hydrateDraft = (rows: LoadTrailerPlanVehicle[]) => {
    const next: Record<string, VehicleDraft> = {}
    for (const vehicle of rows) {
      next[vehicle.vehicleId] = {
        trailerPosition: vehicle.trailerPosition ?? '',
        pickupSequence: vehicle.pickupSequence != null ? String(vehicle.pickupSequence) : '',
        deliverySequence: vehicle.deliverySequence != null ? String(vehicle.deliverySequence) : '',
        notes: vehicle.notes ?? '',
      }
    }
    setVehicleDraft(next)
  }

  const hydrateLevers = (value?: Record<string, number>) => {
    const next: Record<string, string> = {}
    for (const [key, raw] of Object.entries(value ?? {})) {
      if (Number.isFinite(raw)) next[key] = String(raw)
    }
    setLeverHeights(next)
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payloadVehicles = vehicleOrder.map((vehicleId) => {
        const fallback = vehicles.find((vehicle) => vehicle.vehicleId === vehicleId)
        const draft = vehicleDraft[vehicleId] ?? {
          trailerPosition: fallback?.trailerPosition ?? '',
          pickupSequence: fallback?.pickupSequence != null ? String(fallback.pickupSequence) : '',
          deliverySequence: fallback?.deliverySequence != null ? String(fallback.deliverySequence) : '',
          notes: fallback?.notes ?? '',
        }

        return {
          vehicleId,
          trailerPosition: draft.trailerPosition || null,
          pickupSequence: draft.pickupSequence ? Number(draft.pickupSequence) : null,
          deliverySequence: draft.deliverySequence ? Number(draft.deliverySequence) : null,
          notes: draft.notes || null,
          leverHeight: draft.trailerPosition && leverHeights[draft.trailerPosition] ? Number(clampLeverValue(leverHeights[draft.trailerPosition])) : null,
        }
      })

      const normalizedSlotProfiles = Object.fromEntries(
        Object.entries(slotProfiles)
          .map(([slotId, profile]) => {
            const maxLengthInches = profile.maxLengthInches ? Number(profile.maxLengthInches) : undefined
            const maxHeightInches = profile.maxHeightInches ? Number(profile.maxHeightInches) : undefined
            const maxWeightLbs = profile.maxWeightLbs ? Number(profile.maxWeightLbs) : undefined
            return [slotId, {
              maxLengthInches: Number.isFinite(maxLengthInches as number) ? maxLengthInches : undefined,
              maxHeightInches: Number.isFinite(maxHeightInches as number) ? maxHeightInches : undefined,
              maxWeightLbs: Number.isFinite(maxWeightLbs as number) ? maxWeightLbs : undefined,
              allowInoperable: Boolean(profile.allowInoperable),
            }]
          })
      )

      return (await api.loads.saveTrailerPlan(loadId!, {
        ...{ expectedUpdatedAt: draftBase?.updatedAt ?? null },
        plannerStatus,
        plannerNotes,
        equipmentLayoutType: layoutMode,
        trailerDiagramMode: layoutMode,
        leverHeights: Object.fromEntries(Object.entries(leverHeights).map(([key, value]) => [key, value ? Number(clampLeverValue(value)) : null])),
        deckStates,
        rampStates,
        slotProfiles: normalizedSlotProfiles,
        vehicles: payloadVehicles,
      })).data
    },
    onSuccess: (data) => {
      setDraftBase({ loadId: data.loadId, updatedAt: data.planner?.updatedAt ?? null })
      setPlannerStatus(data.planner?.status || 'DRAFT')
      setPlannerNotes(data.planner?.notes || '')
      setLayoutMode(data.planner?.equipmentLayoutType || data.layoutDescriptor.layoutId)
      hydrateLevers(data.planner?.leverHeights)
      setDeckStates(data.planner?.deckStates ?? {})
      setRampStates(data.planner?.rampStates ?? {})
      hydrateDraft(data.vehicles)
      setSlotProfiles(buildSlotProfileDraft(data.layoutDescriptor.positions, data.planner?.slotProfiles))
      setVehicleOrder(data.vehicles.map((vehicle) => vehicle.vehicleId))
      setIsDirty(false)
      setSaveFeedback('saved')
      setSaveFeedbackText('Trailer plan saved.')
      if (loadId) publishLoadStatusChanged(loadId)
      qc.setQueryData(['load-trailer-plan', loadId], data)
    },
    onError: (error: any) => {
      setSaveFeedback('error')
      setSaveFeedbackText(error?.response?.data?.error ?? 'Could not save trailer plan. Check connection and try again.')
    },
  })

  const autoMutation = useMutation({
    mutationFn: async () => (await api.loads.autoSequenceTrailerPlan(loadId!, { vehicleOrder })).data,
    onSuccess: (data) => {
      setPlannerStatus(data.planner?.status || 'AUTO_SEQUENCED')
      setPlannerNotes(data.planner?.notes || '')
      setLayoutMode(data.planner?.equipmentLayoutType || data.layoutDescriptor.layoutId)
      hydrateLevers(data.planner?.leverHeights)
      hydrateDraft(data.vehicles)
      setSlotProfiles(buildSlotProfileDraft(data.layoutDescriptor.positions, data.planner?.slotProfiles))
      setVehicleOrder(data.vehicles.map((vehicle) => vehicle.vehicleId))
      setIsDirty(false)
      setSaveFeedback('saved')
      setSaveFeedbackText('Auto sequence applied.')
      qc.setQueryData(['load-trailer-plan', loadId], data)
    },
    onError: () => {
      setSaveFeedback('error')
      setSaveFeedbackText('Auto sequence failed. Try again or reorder units manually.')
    },
  })

  const [magicResult, setMagicResult] = useState<any | null>(null)
  const [magicPending, setMagicPending] = useState(false)
  const [applyPending, setApplyPending] = useState(false)

  const normalizeMagicPayload = (raw: any) => {
    if (!raw) return null
    // unwrap common axios/response wrappers (response -> response.data -> response.data.data)
    let payload = raw
    try {
      // axios response
      if (payload && typeof payload === 'object' && 'data' in payload) payload = payload.data
      // double-wrapped
      if (payload && typeof payload === 'object' && 'data' in payload) payload = payload.data
    } catch (e) {
      // ignore
    }
    return payload
  }

  const runMagicPlan = async () => {
    if (!canEditTrailerPlan) return
    setMagicPending(true)
    try {
      const r = await api.loads.magicTrailerPlan(loadId!)
      const payload = normalizeMagicPayload(r)
      setMagicResult(payload)
    } catch (err) {
      setMagicResult({ error: 'Could not compute magic plan' })
    } finally {
      setMagicPending(false)
    }
  }

  const applyMagicPlan = async () => {
    if (!canEditTrailerPlan) return
    const result = normalizeMagicPayload(magicResult) ?? magicResult
    const recommendations = result?.recommendations
    const statusToApply = result?.status
    if (!recommendations) return
    if (!window.confirm('Apply recommended Magic Plan to this trailer? This will update positions and lever heights.')) return
    setApplyPending(true)
    try {
      const r = await api.loads.applyMagicTrailerPlan(loadId!, { recommendations, status: statusToApply })
      const payload = normalizeMagicPayload(r)
      qc.setQueryData(['load-trailer-plan', loadId], payload ?? r.data ?? r)
      setMagicResult(null)
      setSaveFeedback('saved')
      setSaveFeedbackText('Magic plan applied')
    } catch (err) {
      setSaveFeedback('error')
      setSaveFeedbackText('Applying magic plan failed')
    } finally {
      setApplyPending(false)
    }
  }

  const runDeterministicRecommendation = async () => {
    if (!canEditTrailerPlan) return
    setRecommendPending(true)
    try {
      const response = await api.loads.recommendTrailerPlan(loadId!, { apply: false })
      setRecommendation(response.data)
    } catch {
      setRecommendation(null)
      setSaveFeedback('error')
      setSaveFeedbackText('Failed to build deterministic recommendation.')
    } finally {
      setRecommendPending(false)
    }
  }

  const applyDeterministicRecommendation = async () => {
    if (!canEditTrailerPlan) return
    if (!recommendation) return
    if (!window.confirm('Apply deterministic recommendation and persist it as a trip layout snapshot?')) return
    setApplyRecommendationPending(true)
    try {
      await api.loads.recommendTrailerPlan(loadId!, {
        apply: true,
        name: tripLayoutName.trim() || `Deterministic ${new Date().toLocaleString()}`,
      })
      await qc.invalidateQueries({ queryKey: ['load-trailer-plan', loadId] })
      setSaveFeedback('saved')
      setSaveFeedbackText('Deterministic recommendation applied and persisted.')
      setRecommendation(null)
    } catch {
      setSaveFeedback('error')
      setSaveFeedbackText('Failed to apply deterministic recommendation.')
    } finally {
      setApplyRecommendationPending(false)
    }
  }

  const saveTripLayoutSnapshot = async () => {
    if (!canEditTrailerPlan) return
    const name = tripLayoutName.trim()
    if (!name) {
      setSaveFeedback('error')
      setSaveFeedbackText('Enter a trip layout name before saving a snapshot.')
      return
    }
    try {
      await api.loads.saveTripLayoutSnapshot(loadId!, { name })
      await qc.invalidateQueries({ queryKey: ['load-trailer-plan', loadId] })
      setSaveFeedback('saved')
      setSaveFeedbackText('Trip layout snapshot saved.')
      setTripLayoutName('')
    } catch {
      setSaveFeedback('error')
      setSaveFeedbackText('Failed to save trip layout snapshot.')
    }
  }

  const applyTripLayoutSnapshot = async (layoutId: string) => {
    if (!canEditTrailerPlan) return
    setApplyLayoutPendingId(layoutId)
    try {
      await api.loads.applyTripLayoutSnapshot(loadId!, layoutId)
      await qc.invalidateQueries({ queryKey: ['load-trailer-plan', loadId] })
      setSaveFeedback('saved')
      setSaveFeedbackText('Trip layout applied.')
    } catch {
      setSaveFeedback('error')
      setSaveFeedbackText('Failed to apply selected trip layout.')
    } finally {
      setApplyLayoutPendingId(null)
    }
  }

  const postLoadMutation = useMutation({
    mutationFn: async () => api.loads.post(loadId!, { notes: assignmentNotes || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['load-assignment-options', loadId] })
      qc.invalidateQueries({ queryKey: ['load', loadId] })
    },
  })

  const assignCarrierMutation = useMutation({
    mutationFn: async () => api.loads.assignCarrier(loadId!, { carrierId: selectedCarrierId || undefined, notes: assignmentNotes || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['load-assignment-options', loadId] })
      qc.invalidateQueries({ queryKey: ['load', loadId] })
    },
  })

  const assignDriverMutation = useMutation({
    mutationFn: async () => api.loads.assignDriver(loadId!, {
      driverUserId: selectedDriverId || undefined,
      carrierId: selectedCarrierId || undefined,
      notes: assignmentNotes || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['load-assignment-options', loadId] })
      qc.invalidateQueries({ queryKey: ['load', loadId] })
    },
  })

  useEffect(() => {
    const plan = planQuery.data
    if (!plan) return
    if (isDirty && draftBase?.loadId === plan.loadId) return
    setDraftBase({ loadId: plan.loadId, updatedAt: plan.planner?.updatedAt ?? null })
    setPlannerStatus(plan.planner?.status || 'DRAFT')
    setPlannerNotes(plan.planner?.notes || '')
    setLayoutMode(plan.planner?.equipmentLayoutType || plan.layoutDescriptor.layoutId)
    hydrateLevers(plan.planner?.leverHeights)
    setDeckStates(plan.planner?.deckStates ?? {})
    setRampStates(plan.planner?.rampStates ?? {})
    hydrateDraft(plan.vehicles)
    setSlotProfiles(buildSlotProfileDraft(plan.layoutDescriptor.positions, plan.planner?.slotProfiles))
    setVehicleOrder(plan.vehicles.map((vehicle) => vehicle.vehicleId))
    setSelectedSlotId(null)
    setIsDirty(false)
  }, [planQuery.data?.loadId, planQuery.data?.planner?.updatedAt])

  const markDirty = () => {
    setIsDirty(true)
    if (saveFeedback !== 'idle') {
      setSaveFeedback('idle')
      setSaveFeedbackText('')
    }
  }

  const orderedVehicles = useMemo(() => {
    const byId = new Map(vehicles.map((vehicle) => [vehicle.vehicleId, vehicle]))
    const fromOrder = vehicleOrder.map((vehicleId) => byId.get(vehicleId)).filter(Boolean) as LoadTrailerPlanVehicle[]
    const remainder = vehicles.filter((vehicle) => !vehicleOrder.includes(vehicle.vehicleId))
    return [...fromOrder, ...remainder]
  }, [vehicleOrder, vehicles])

  const slotToVehicle = useMemo(() => {
    const map = new Map<string, LoadTrailerPlanVehicle>()
    for (const vehicle of orderedVehicles) {
      const slot = vehicleDraft[vehicle.vehicleId]?.trailerPosition || vehicle.trailerPosition || ''
      const draft = vehicleDraft[vehicle.vehicleId]
      if (slot) map.set(slot, { ...vehicle,
        pickupSequence: draft ? Number(draft.pickupSequence) || null : vehicle.pickupSequence,
        deliverySequence: draft ? Number(draft.deliverySequence) || null : vehicle.deliverySequence,
      })
    }
    return map
  }, [orderedVehicles, vehicleDraft])

  if (planQuery.isLoading) {
    return <div className="container mx-auto p-6 text-gray-500">Loading trailer loader...</div>
  }

  if (!planQuery.data) {
    return <div className="container mx-auto p-6 text-red-600">Unable to load trailer plan.</div>
  }

  const plan = planQuery.data
  const layout = plan.layoutDescriptor
  const totalVehicleWeight = orderedVehicles.reduce((sum, vehicle) => sum + (Number(vehicle.weightLb) || 0), 0)
  const slotWeightCapacity = layout.positions.reduce((sum, position) => sum + (Number(position.maxWeightLbs) || 0), 0)
  const payloadUtilization = slotWeightCapacity > 0 ? Math.round((totalVehicleWeight / slotWeightCapacity) * 1000) / 10 : null
  const slotUsage = new Map<string, string[]>()
  for (const vehicle of orderedVehicles) {
    const slot = normalizeSlot(vehicleDraft[vehicle.vehicleId]?.trailerPosition || vehicle.trailerPosition || '')
    if (!slot) continue
    const existing = slotUsage.get(slot) ?? []
    slotUsage.set(slot, [...existing, vehicle.vehicleId])
  }
  const duplicateSlots = Array.from(slotUsage.entries()).filter(([, ids]) => ids.length > 1).map(([slot]) => slot)

  const localWarnings: string[] = []
  if (duplicateSlots.length > 0) {
    localWarnings.push(`Duplicate slot assignments detected: ${duplicateSlots.join(', ')}.`)
  }
  const missingSequenceCount = orderedVehicles.filter((vehicle) => {
    const draft = vehicleDraft[vehicle.vehicleId]
    const slot = normalizeSlot(draft?.trailerPosition || vehicle.trailerPosition || '')
    if (!slot) return false
    const pickup = draft?.pickupSequence ?? (vehicle.pickupSequence != null ? String(vehicle.pickupSequence) : '')
    const delivery = draft?.deliverySequence ?? (vehicle.deliverySequence != null ? String(vehicle.deliverySequence) : '')
    return !pickup || !delivery
  }).length
  if (missingSequenceCount > 0) {
    localWarnings.push(`Missing sequence values on ${missingSequenceCount} assigned unit(s). Add pickup and delivery sequence for clearer load/unload order.`)
  }

  const allWarnings = Array.from(new Set([...(plan.warnings ?? []), ...(plan.planner?.warnings ?? []), ...localWarnings]))
  const warningGroups: Record<WarningSeverity, string[]> = { critical: [], warning: [], info: [] }
  for (const warning of allWarnings) {
    warningGroups[warningSeverity(warning)].push(warning)
  }
  const warningCounts = {
    critical: warningGroups.critical.length,
    warning: warningGroups.warning.length,
    info: warningGroups.info.length,
  }

  const leverErrors: Record<string, string> = {}
  for (const position of layout.positions) {
    const raw = leverHeights[position.id] ?? ''
    if (!raw) continue
    const value = Number(raw)
    if (!Number.isFinite(value) || value < 1 || value > 7) {
      leverErrors[position.id] = 'Lever must be between 1 and 7.'
    }
  }

  const setVehiclePosition = (vehicleId: string, nextRaw: string) => {
    if (!canEditTrailerPlan) return
    const nextPosition = normalizeSlot(nextRaw)
    setVehicleDraft((prev) => {
      const next = { ...prev }
      const current = next[vehicleId] ?? { trailerPosition: '', pickupSequence: '', deliverySequence: '', notes: '' }
      for (const [otherVehicleId, draft] of Object.entries(next)) {
        if (otherVehicleId !== vehicleId && normalizeSlot(draft.trailerPosition) === nextPosition && nextPosition) {
          next[otherVehicleId] = { ...draft, trailerPosition: '' }
        }
      }
      next[vehicleId] = { ...current, trailerPosition: nextPosition }
      return next
    })
    setSelectedSlotId(nextPosition || null)
    markDirty()
  }

  const selectedVehicle = selectedVehicleId ? orderedVehicles.find((vehicle) => vehicle.vehicleId === selectedVehicleId) : null
  const hasAssignedEquipment = Boolean(
    plan.assignedEquipment
    && [
      plan.assignedEquipment.driverName,
      plan.assignedEquipment.powerUnitModel,
      plan.assignedEquipment.trailerModel,
      plan.assignedEquipment.trailerNumber,
      plan.assignedEquipment.equipmentType,
      plan.assignedEquipment.layoutType,
    ].some((value) => Boolean(value)),
  )

  const assignSlot = (position: TrailerLayoutPosition) => {
    if (!canEditTrailerPlan) return
    setSelectedSlotId(position.id)
    const occupied = slotToVehicle.get(position.id)
    if (!selectedVehicleId) {
      if (occupied) setSelectedVehicleId(occupied.vehicleId)
      return
    }

    setVehiclePosition(selectedVehicleId, position.id)
  }

  const clearPlan = () => {
    if (!canEditTrailerPlan) return
    if (!window.confirm('Clear assignments only for this trailer plan?')) return
    setVehicleDraft((prev) => {
      const next = { ...prev }
      for (const key of Object.keys(next)) {
        next[key] = { ...next[key], trailerPosition: '', pickupSequence: '', deliverySequence: '' }
      }
      return next
    })
    setSelectedSlotId(null)
    setLeverHeights({})
    markDirty()
  }

  const moveVehicle = (vehicleId: string, direction: -1 | 1) => {
    if (!canEditTrailerPlan) return
    setVehicleOrder((prev) => {
      const current = prev.indexOf(vehicleId)
      const next = current + direction
      if (current < 0 || next < 0 || next >= prev.length) return prev
      const clone = [...prev]
      const [item] = clone.splice(current, 1)
      clone.splice(next, 0, item)
      return clone
    })
    markDirty()
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trailer Loader</h1>
          <p className="text-sm text-gray-500 mt-1">Trailer Planner</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/loads/${plan.loadId}`} className="border rounded px-3 py-2 text-sm">Back to Load</Link>
          <button onClick={() => navigate('/dispatch-board')} className="border rounded px-3 py-2 text-sm">Dispatch Board</button>
        </div>
      </div>

      {isSupportReadOnlyView && <SupportReadOnlyNotice className="mb-4" />}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800">
          <div className="font-semibold">Critical</div>
          <div>{warningCounts.critical}</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
          <div className="font-semibold">Warnings</div>
          <div>{warningCounts.warning}</div>
        </div>
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sky-900">
          <div className="font-semibold">Info</div>
          <div>{warningCounts.info}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700">
          <div className="font-semibold">Payload Utilization</div>
          <div>{payloadUtilization != null ? `${payloadUtilization}%` : '—'}</div>
        </div>
      </div>

      {(allWarnings.length > 0 || saveFeedback === 'error') && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-2">
          {warningGroups.critical.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-red-700">Critical</div>
              {warningGroups.critical.map((warning) => (
                <div key={`critical-${warning}`} className="rounded-md border border-red-300 bg-red-50 px-2 py-1 text-red-800">{warning}</div>
              ))}
            </div>
          )}
          {warningGroups.warning.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">Warning</div>
              {warningGroups.warning.map((warning) => (
                <div key={`warning-${warning}`} className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-amber-900">{warning}</div>
              ))}
            </div>
          )}
          {warningGroups.info.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">Info</div>
              {warningGroups.info.map((warning) => (
                <div key={`info-${warning}`} className="rounded-md border border-sky-300 bg-sky-50 px-2 py-1 text-sky-900">{warning}</div>
              ))}
            </div>
          )}
          {saveFeedback === 'error' && <div className="rounded-md border border-red-300 bg-red-50 px-2 py-1 text-red-800">{saveFeedbackText}</div>}
        </div>
      )}

      <fieldset disabled={isSupportReadOnlyView || isTerminalPlan} className="space-y-5 border-0 p-0 m-0">
        <legend className="sr-only">Trailer planner controls</legend>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Selected Units</h2>
            <div className="text-xs text-gray-500">
              {canEditTrailerPlan
                ? 'Drag to reorder units or use Auto Sequence to optimize load order.'
                : 'Support view is read-only, so assignment and sequencing controls are disabled.'}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-blue-800">
              Selected vehicle: {selectedVehicle ? (selectedVehicle.vin || vehicleLabel(selectedVehicle)) : 'None'}
            </span>
            <span className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800">
              Selected slot: {selectedSlotId || 'None'}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b text-left">
                <tr>
                  <th className="px-2 py-2">Move</th>
                  <th className="px-2 py-2">Year</th>
                  <th className="px-2 py-2">Make</th>
                  <th className="px-2 py-2">Model</th>
                  <th className="px-2 py-2">VIN</th>
                  <th className="px-2 py-2">Position</th>
                  <th className="px-2 py-2">Pickup</th>
                  <th className="px-2 py-2">Delivery</th>
                </tr>
              </thead>
              <tbody>
                {orderedVehicles.map((vehicle, index) => {
                  const draft = vehicleDraft[vehicle.vehicleId] ?? { trailerPosition: '', pickupSequence: '', deliverySequence: '', notes: '' }
                  return (
                    <tr
                      key={vehicle.vehicleId}
                      className={`border-b last:border-b-0 ${canEditTrailerPlan ? 'cursor-pointer' : 'cursor-default'} ${selectedVehicleId === vehicle.vehicleId ? 'bg-blue-50 ring-1 ring-inset ring-blue-300' : ''}`}
                      onClick={canEditTrailerPlan ? () => {
                        setSelectedVehicleId(vehicle.vehicleId)
                        const slot = normalizeSlot(draft.trailerPosition || vehicle.trailerPosition || '')
                        setSelectedSlotId(slot || null)
                      } : undefined}
                    >
                      <td className="px-2 py-2">
                        <div className="flex gap-1">
                          <button className="border rounded px-1" onClick={() => moveVehicle(vehicle.vehicleId, -1)} disabled={index === 0 || !canEditTrailerPlan}>↑</button>
                          <button className="border rounded px-1" onClick={() => moveVehicle(vehicle.vehicleId, 1)} disabled={index === orderedVehicles.length - 1 || !canEditTrailerPlan}>↓</button>
                        </div>
                      </td>
                      <td className="px-2 py-2">{vehicle.year ?? '—'}</td>
                      <td className="px-2 py-2">{vehicle.make ?? '—'}</td>
                      <td className="px-2 py-2">{vehicle.model ?? '—'}</td>
                      <td className="px-2 py-2 font-mono text-xs">{vehicle.vin || '—'}</td>
                      <td className="px-2 py-2">
                        <select
                          className="border rounded px-2 py-1 w-28"
                          value={normalizeSlot(draft.trailerPosition)}
                          onChange={(e) => setVehiclePosition(vehicle.vehicleId, e.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {layout.positions.map((position) => (
                            <option key={position.id} value={position.id}>{position.label}</option>
                          ))}
                          {draft.trailerPosition && !layout.positions.some((position) => position.id === normalizeSlot(draft.trailerPosition)) && (
                            <option value={normalizeSlot(draft.trailerPosition)}>{normalizeSlot(draft.trailerPosition)}</option>
                          )}
                        </select>
                      </td>
                      <td className="px-2 py-2"><input className="border rounded px-2 py-1 w-14" value={draft.pickupSequence} onChange={(e) => { setVehicleDraft((prev) => ({ ...prev, [vehicle.vehicleId]: { ...draft, pickupSequence: e.target.value.replace(/[^0-9]/g, '') } })); markDirty() }} /></td>
                      <td className="px-2 py-2"><input className="border rounded px-2 py-1 w-14" value={draft.deliverySequence} onChange={(e) => { setVehicleDraft((prev) => ({ ...prev, [vehicle.vehicleId]: { ...draft, deliverySequence: e.target.value.replace(/[^0-9]/g, '') } })); markDirty() }} /></td>
                    </tr>
                  )
                })}
                {!orderedVehicles.length && (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={8}>No units on this load.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-2">
            <button className="border rounded px-3 py-2 text-sm opacity-60" disabled>Add Units</button>
            <button onClick={() => autoMutation.mutate()} disabled={autoMutation.isPending || !canEditTrailerPlan} className="border rounded px-3 py-2 text-sm">{autoMutation.isPending ? 'Auto-sequencing...' : 'Auto Sequence'}</button>
            <button onClick={runDeterministicRecommendation} disabled={recommendPending || !canEditTrailerPlan} className="border rounded px-3 py-2 text-sm">{recommendPending ? 'Computing...' : 'Deterministic Recommend'}</button>
            <button onClick={() => runMagicPlan()} disabled={magicPending || !canEditTrailerPlan} className="border rounded px-3 py-2 text-sm">{magicPending ? 'Thinking...' : 'Magic Plan'}</button>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">Driver / Assigned Equipment</h2>
          <div className="text-sm text-gray-700 space-y-1">
            <div><span className="text-gray-500">Driver Name:</span> {plan.assignedEquipment?.driverName || 'Unassigned'}</div>
            <div><span className="text-gray-500">Power Unit / Tractor Model:</span> {plan.assignedEquipment?.powerUnitModel || '—'}</div>
            <div><span className="text-gray-500">Trailer Model:</span> {plan.assignedEquipment?.trailerModel || '—'}</div>
            <div><span className="text-gray-500">Trailer #:</span> {plan.assignedEquipment?.trailerNumber || '—'}</div>
            <div><span className="text-gray-500">Equipment Type:</span> {plan.assignedEquipment?.equipmentType || 'Generic'}</div>
            <div><span className="text-gray-500">Trailer Capacity:</span> {plan.layoutDescriptor.capacity}</div>
            <div><span className="text-gray-500">Projected Payload:</span> {totalVehicleWeight.toLocaleString()} lb</div>
            <div><span className="text-gray-500">Slot Weight Capacity:</span> {slotWeightCapacity.toLocaleString()} lb</div>
            <div><span className="text-gray-500">Utilization:</span> {payloadUtilization != null ? `${payloadUtilization}%` : '—'}</div>
          </div>
          <div className="text-xs text-gray-500">
            {hasAssignedEquipment
              ? 'Diagram adapts to assigned equipment.'
              : 'No assigned equipment found. Showing generic trailer layout.'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white border rounded-xl p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Trailer Diagram</h2>
            <div className="text-xs text-gray-500">{layout.layoutName}</div>
          </div>
          <div className="rounded-xl border bg-gray-50 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-24 rounded bg-slate-700 text-white text-xs px-2 py-2">SEMI</div>
              <div className="text-xs text-gray-500">{layout.tandemMetadata?.axleLabels.join(', ') || 'Axle labels unavailable'}</div>
            </div>
            {/* Trailer diagram SVG: simplified semi + 10-position car hauler */}
            <div className="w-full overflow-auto py-2">
              <TrailerDiagram
                onMove={canEditTrailerPlan ? (vehicleId, slot) => {
                  setVehicleDraft(prev => ({ ...prev, [vehicleId]: { ...prev[vehicleId], trailerPosition: slot } }))
                  markDirty()
                } : undefined}
                positions={layout.positions}
                slotToVehicle={slotToVehicle}
                equipmentLabel={[plan.assignedEquipment?.trailerModel, plan.assignedEquipment?.trailerNumber].filter(Boolean).join(' · ') || plan.assignedEquipment?.equipmentType || null}
                geometry={plan.assignedEquipment?.geometry}
                deckStates={deckStates}
                rampStates={rampStates}
                onDeckStateChange={(id, state) => { setDeckStates(previous => ({ ...previous, [id]: state })); markDirty() }}
                onRampStateChange={(id, state) => { setRampStates(previous => ({ ...previous, [id]: state })); markDirty() }}
                selectedSlotId={selectedSlotId}
                onSelectSlot={assignSlot}
                readOnly={!canEditTrailerPlan}
              />
            </div>

            <div className="text-xs text-gray-500">Level scale {layout.levelScale?.min ?? 1}-{layout.levelScale?.max ?? 10}. Sliding tandems: {layout.tandemMetadata?.supportsSlidingTandems ? 'enabled' : 'not available'}.</div>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Lever Height</h2>
            <p className="text-xs text-gray-500">1 = lowest / full down. 7 = highest / full up.</p>
          </div>
          <div className="space-y-2 max-h-72 overflow-auto pr-1">
            {layout.positions.map((position) => (
              <div key={position.id} className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600">Position {position.label}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="border rounded px-2 py-1 text-xs"
                      onClick={() => {
                        const current = Number(leverHeights[position.id] || '1')
                        const next = String(Math.max(1, current - 1))
                        setLeverHeights((prev) => ({ ...prev, [position.id]: next }))
                        markDirty()
                      }}
                    >
                      -
                    </button>
                    <input
                      className={`w-16 border rounded px-2 py-1 text-sm ${leverErrors[position.id] ? 'border-red-500 bg-red-50' : ''}`}
                      value={leverHeights[position.id] ?? ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '')
                        setLeverHeights((prev) => ({ ...prev, [position.id]: value }))
                        markDirty()
                      }}
                      onBlur={() => {
                        const clamped = clampLeverValue(leverHeights[position.id] ?? '')
                        setLeverHeights((prev) => ({ ...prev, [position.id]: clamped }))
                      }}
                    />
                    <button
                      type="button"
                      className="border rounded px-2 py-1 text-xs"
                      onClick={() => {
                        const current = Number(leverHeights[position.id] || '1')
                        const next = String(Math.min(7, current + 1))
                        setLeverHeights((prev) => ({ ...prev, [position.id]: next }))
                        markDirty()
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
                {leverErrors[position.id] && <div className="text-[11px] text-red-600">{leverErrors[position.id]}</div>}
              </div>
            ))}
          </div>

          <div className="rounded-lg border bg-gray-50 p-3 text-xs text-gray-600 space-y-1">
            <div className="font-semibold text-gray-700">Legend</div>
            <div>Solid: assigned trailer position</div>
            <div>Dashed: empty slot</div>
            <div>Warning badge appears in top warnings panel</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Configurable Slot Profiles</h2>
            <div className="text-xs text-gray-500">Overrides slot constraints per position</div>
          </div>
          <div className="max-h-80 overflow-auto pr-1 space-y-2">
            {layout.positions.map((position) => {
              const draft = slotProfiles[position.id] ?? {
                maxLengthInches: String(position.maxLengthInches ?? ''),
                maxHeightInches: String(position.maxHeightInches ?? ''),
                maxWeightLbs: String(position.maxWeightLbs ?? ''),
                allowInoperable: Boolean(position.allowInoperable),
              }
              return (
                <div key={`slot-profile-${position.id}`} className="rounded-lg border p-2 space-y-2">
                  <div className="text-xs font-semibold text-gray-700">Slot {position.label} ({position.deck})</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <input
                      className="border rounded px-2 py-1"
                      placeholder="Max length"
                      value={draft.maxLengthInches}
                      onChange={(e) => { setSlotProfiles((prev) => ({ ...prev, [position.id]: { ...draft, maxLengthInches: e.target.value.replace(/[^0-9]/g, '') } })); markDirty() }}
                    />
                    <input
                      className="border rounded px-2 py-1"
                      placeholder="Max height"
                      value={draft.maxHeightInches}
                      onChange={(e) => { setSlotProfiles((prev) => ({ ...prev, [position.id]: { ...draft, maxHeightInches: e.target.value.replace(/[^0-9]/g, '') } })); markDirty() }}
                    />
                    <input
                      className="border rounded px-2 py-1"
                      placeholder="Max weight"
                      value={draft.maxWeightLbs}
                      onChange={(e) => { setSlotProfiles((prev) => ({ ...prev, [position.id]: { ...draft, maxWeightLbs: e.target.value.replace(/[^0-9]/g, '') } })); markDirty() }}
                    />
                    <label className="flex items-center gap-1 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={draft.allowInoperable}
                        onChange={(e) => { setSlotProfiles((prev) => ({ ...prev, [position.id]: { ...draft, allowInoperable: e.target.checked } })); markDirty() }}
                      />
                      Inop Allowed
                    </label>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Trip Layout Snapshots</h2>
            <div className="text-xs text-gray-500">Persisted dispatcher layouts</div>
          </div>
          <div className="flex gap-2">
            <input
              className="flex-1 border rounded px-2 py-2 text-sm"
              value={tripLayoutName}
              onChange={(e) => setTripLayoutName(e.target.value)}
              placeholder="Snapshot name"
            />
            <button className="border rounded px-3 py-2 text-sm" onClick={saveTripLayoutSnapshot}>Save Snapshot</button>
          </div>

          <div className="space-y-2 max-h-40 overflow-auto pr-1">
            {(plan.planner?.tripLayouts ?? []).map((layoutSnapshot) => (
              <div key={layoutSnapshot.id} className="rounded-lg border p-2 flex items-center justify-between gap-2 text-xs">
                <div>
                  <div className="font-semibold text-gray-800">{layoutSnapshot.name}</div>
                  <div className="text-gray-500">{new Date(layoutSnapshot.createdAt).toLocaleString()}</div>
                </div>
                <button
                  className="border rounded px-2 py-1"
                  onClick={() => applyTripLayoutSnapshot(layoutSnapshot.id)}
                  disabled={applyLayoutPendingId === layoutSnapshot.id}
                >
                  {applyLayoutPendingId === layoutSnapshot.id ? 'Applying...' : 'Apply'}
                </button>
              </div>
            ))}
            {!(plan.planner?.tripLayouts ?? []).length && (
              <div className="text-xs text-gray-500">No saved snapshots yet.</div>
            )}
          </div>

          {recommendation && (
            <div className="rounded-lg border bg-sky-50 p-3 space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sky-900">Deterministic Recommendation</div>
                <div className="text-sky-800">Score {recommendation.score}</div>
              </div>
              <div className="text-sky-900">Status: {recommendation.status}</div>
              {!!recommendation.warnings.length && (
                <div className="space-y-1">
                  {recommendation.warnings.slice(0, 3).map((warning) => (
                    <div key={warning} className="rounded border border-sky-200 bg-white px-2 py-1">{warning}</div>
                  ))}
                </div>
              )}
              <div className="max-h-28 overflow-auto pr-1 space-y-1">
                {recommendation.recommendations.slice(0, 10).map((item) => (
                  <div key={`reco-${item.vehicleId}`} className="rounded border border-sky-200 bg-white px-2 py-1">
                    {(item.vin || item.vehicleId)} {'->'} {item.recommendedPosition || 'unassigned'} ({Math.round(item.confidence * 100)}%)
                  </div>
                ))}
              </div>
              <button
                className="border rounded px-3 py-2 text-xs bg-white"
                onClick={applyDeterministicRecommendation}
                disabled={applyRecommendationPending || !canEditTrailerPlan}
              >
                {applyRecommendationPending ? 'Applying...' : 'Apply Recommendation'}
              </button>
            </div>
          )}
        </div>
      </div>

        <div className="bg-white border rounded-xl p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Planner Status</label>
            <select className="w-full border rounded px-2 py-2" value={plannerStatus} onChange={(e) => { setPlannerStatus(e.target.value); markDirty() }}>
              {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Diagram Mode</label>
            <input className="w-full border rounded px-2 py-2" value={layoutMode} onChange={(e) => { setLayoutMode(e.target.value); markDirty() }} />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-gray-500 block mb-1">Notes</label>
            <textarea className="w-full border rounded px-2 py-2" rows={3} value={plannerNotes} onChange={(e) => { setPlannerNotes(e.target.value); markDirty() }} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
          <select className="border rounded px-2 py-2" value={selectedCarrierId} onChange={(e) => setSelectedCarrierId(e.target.value)}>
            <option value="">Assign Carrier</option>
            {(assignmentQuery.data?.carriers ?? []).map((carrier: any) => (
              <option key={carrier.id} value={carrier.id}>{carrier.legal_name || carrier.dba_name || carrier.id}</option>
            ))}
          </select>
          <select className="border rounded px-2 py-2" value={selectedDriverId} onChange={(e) => setSelectedDriverId(e.target.value)}>
            <option value="">Assign Driver</option>
            {(assignmentQuery.data?.drivers ?? []).map((driver: any) => (
              <option key={driver.aux_user_id || driver.id} value={driver.aux_user_id || driver.id}>{driver.legal_name || [driver.first_name, driver.last_name].filter(Boolean).join(' ') || driver.aux_email || driver.id}</option>
            ))}
          </select>
          <input className="border rounded px-2 py-2" value={assignmentNotes} onChange={(e) => setAssignmentNotes(e.target.value)} placeholder="Assignment notes" />
          <div className="flex gap-2">
            <button onClick={() => assignCarrierMutation.mutate()} disabled={assignCarrierMutation.isPending || !selectedCarrierId} className="border rounded px-3 py-2">Carrier</button>
            <button onClick={() => assignDriverMutation.mutate()} disabled={assignDriverMutation.isPending || !selectedDriverId} className="border rounded px-3 py-2">Driver</button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 flex-wrap text-sm border-t pt-3">
          <div className="text-xs text-gray-600">
            {saveFeedback === 'error'
              ? saveFeedbackText
              : isDirty
                ? 'Unsaved changes'
                : saveFeedback === 'saved'
                  ? saveFeedbackText || 'All changes saved'
                  : 'All changes saved'}
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={clearPlan} className="border rounded px-3 py-2" disabled={!canEditTrailerPlan}>Clear Plan</button>
            <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !canEditTrailerPlan} className="bg-blue-700 text-white rounded px-3 py-2">{saveMutation.isPending ? 'Saving...' : 'Save Plan'}</button>
            <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !canEditTrailerPlan} className="border rounded px-3 py-2">Assign Units</button>
            <button onClick={() => postLoadMutation.mutate()} disabled={postLoadMutation.isPending || !canEditTrailerPlan} className="border rounded px-3 py-2">{postLoadMutation.isPending ? 'Posting...' : 'Post Load'}</button>
            <Link to={`/loads/${plan.loadId}`} className="border rounded px-3 py-2">Open Load Detail</Link>
            {assignmentQuery.data?.load.currentAssignment.jobId && (
              <Link className="border rounded px-3 py-2 text-blue-700" to={`/driver/jobs/${assignmentQuery.data.load.currentAssignment.jobId}`}>Open Driver Job</Link>
            )}
          </div>
        </div>
      </div>

      {(plan.driverNotes?.loadOrder || plan.driverNotes?.unloadOrder) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Load Order</h3>
            <pre className="text-xs text-gray-700 whitespace-pre-wrap">{plan.driverNotes?.loadOrder || '—'}</pre>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Unload Order</h3>
            <pre className="text-xs text-gray-700 whitespace-pre-wrap">{plan.driverNotes?.unloadOrder || '—'}</pre>
          </div>
        </div>
      )}

        {magicResult && (
          <div className="rounded-xl border bg-slate-50 p-4 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Magic Plan Results</h3>
                <p className="text-xs text-gray-500">Explainable trailer-plan recommendations from the Magic Engine.</p>
              </div>
              {'score' in magicResult && (
                <div className="rounded-full bg-white border px-3 py-1 text-xs font-semibold text-gray-700">
                  Score {magicResult.score}
                </div>
              )}
            </div>

            {'status' in magicResult && (
              <div className="text-sm text-gray-700">
                <span className="text-gray-500">Status:</span> {magicResult.status}
              </div>
            )}

            {/* Recommendations block - always render when magicResult exists, with fallbacks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-700">
              <div className="rounded-lg border bg-white p-3 space-y-1">
                <div className="font-semibold text-gray-800">Recommended Positions</div>
                {magicResult.recommendations && Array.isArray(magicResult.recommendations.positions) && magicResult.recommendations.positions.length > 0 ? (
                  <ul className="space-y-1">
                    {magicResult.recommendations.positions.map((recommendation: any) => (
                      <li key={recommendation.vehicleId}>{recommendation.vin || recommendation.vehicleId} → {recommendation.recommendedPosition ?? 'unassigned'}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-500">No recommendations returned.</div>
                )}
              </div>

              <div className="rounded-lg border bg-white p-3 space-y-1">
                <div className="font-semibold text-gray-800">Pickup Sequence</div>
                {magicResult.recommendations && Array.isArray(magicResult.recommendations.pickupSequence) && magicResult.recommendations.pickupSequence.length > 0 ? (
                  <div>{magicResult.recommendations.pickupSequence.join(' · ')}</div>
                ) : (
                  <div className="text-gray-500">No recommendations returned.</div>
                )}
              </div>

              <div className="rounded-lg border bg-white p-3 space-y-1">
                <div className="font-semibold text-gray-800">Delivery Sequence</div>
                {magicResult.recommendations && Array.isArray(magicResult.recommendations.deliverySequence) && magicResult.recommendations.deliverySequence.length > 0 ? (
                  <div>{magicResult.recommendations.deliverySequence.join(' · ')}</div>
                ) : (
                  <div className="text-gray-500">No recommendations returned.</div>
                )}
              </div>

              <div className="rounded-lg border bg-white p-3 space-y-1 md:col-span-2">
                <div className="font-semibold text-gray-800">Lever Heights</div>
                {magicResult.recommendations && magicResult.recommendations.leverHeights && typeof magicResult.recommendations.leverHeights === 'object' && Object.keys(magicResult.recommendations.leverHeights).length > 0 ? (
                  <div>{Object.entries(magicResult.recommendations.leverHeights).map(([positionId, value]) => `${positionId}: ${value}`).join(' · ')}</div>
                ) : (
                  <div className="text-gray-500">No recommendations returned.</div>
                )}
              </div>
            </div>

            <div className="rounded-lg border bg-white p-3 space-y-1 text-xs text-gray-700">
              <div className="font-semibold text-gray-800">Conflicts</div>
              {Array.isArray(magicResult.conflicts) && magicResult.conflicts.length > 0 ? (
                <ul className="space-y-1">
                  {magicResult.conflicts.map((conflict: any, index: number) => (
                    <li key={`${conflict.type || 'conflict'}-${index}`}>{conflict.severity ? `${conflict.severity.toUpperCase()}: ` : ''}{conflict.message || conflict.type || 'Unknown conflict'}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-gray-500">No conflicts detected.</div>
              )}
            </div>

            <div className="rounded-lg border bg-white p-3 space-y-1 text-xs text-gray-700">
              <div className="font-semibold text-gray-800">Explanation</div>
              {Array.isArray(magicResult.explanation) && magicResult.explanation.length > 0 ? (
                <ul className="space-y-1">
                  {magicResult.explanation.map((item: string, index: number) => (
                    <li key={`${index}-${item}`}>{item}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-gray-500">No conflicts detected.</div>
              )}
            </div>

            {'error' in magicResult && magicResult.error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">{magicResult.error}</div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => applyMagicPlan()} disabled={applyPending || !magicResult?.recommendations || !canEditTrailerPlan} className="bg-emerald-600 text-white rounded px-3 py-2 text-sm disabled:opacity-50">{applyPending ? 'Applying...' : 'Apply Magic Plan'}</button>
              <button onClick={() => setMagicResult(null)} className="border rounded px-3 py-2 text-sm">Dismiss</button>
            </div>
          </div>
        )}
      </fieldset>
    </div>
  )
}
